import { type FC, type PropsWithChildren, createContext, useCallback, useContext, useMemo, useState } from 'react'

export type AuthenticationContextProps = {
  isUnauthorized: boolean
  setUnauthorized: () => void
  onFailure: () => void
}

export const AuthenticationContext: React.Context<AuthenticationContextProps | null> =
  createContext<AuthenticationContextProps | null>(null)

export const useAuthenticationContext = (): AuthenticationContextProps => {
  const context = useContext(AuthenticationContext)

  if (!context) {
    throw Error('Component must be wrapped with `AuthenticationContextProvider` component')
  }

  return context
}

export const AuthenticationContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isUnauthorized, setUnauthorized] = useState(false)

  const handleSetUnauthorized = useCallback(() => setUnauthorized(true), [])

  const handleFailure = useCallback(() => {
    throw new Error()
  }, [])

  const context = useMemo<AuthenticationContextProps>(
    () => ({
      isUnauthorized,
      setUnauthorized: handleSetUnauthorized,
      onFailure: handleFailure,
    }),
    [handleSetUnauthorized, handleFailure, isUnauthorized]
  )

  return <AuthenticationContext.Provider value={context}>{children}</AuthenticationContext.Provider>
}
